import { useRef, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

export function ProductGallery({ product, idPrefix = "" }) {
  const images = [...new Set([product.image_url, ...(product.gallery || [])].filter(Boolean))];
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const scrollingTo = useRef(null);

  const go = (i) => {
    const idx = (i + images.length) % images.length;
    setActive(idx);
    const el = trackRef.current;
    if (!el) return;
    const target = idx * el.clientWidth;
    if (Math.abs(el.scrollLeft - target) < 2) return;
    scrollingTo.current = idx;
    setTimeout(() => { scrollingTo.current = null; }, 700);
    el.scrollTo({ left: target, behavior: Math.abs(idx - active) > 1 ? "auto" : "smooth" });
  };

  const onScroll = (e) => {
    const el = e.currentTarget;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (scrollingTo.current !== null) {
      if (idx === scrollingTo.current) scrollingTo.current = null;
      return;
    }
    if (idx !== active) setActive(idx);
  };

  return (
    <div data-testid={`${idPrefix}product-gallery`}>
      <div className="relative rounded-3xl overflow-hidden bg-[#EADDC8]/30 border border-[#E5E0D8]">
        <div ref={trackRef} onScroll={onScroll} className="flex overflow-x-auto snap-x snap-mandatory hh-no-scrollbar">
          {images.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`${product.title} — photo ${i + 1}`}
              data-testid={`${idPrefix}gallery-image-${i}`}
              loading={i === 0 ? "eager" : "lazy"}
              fetchpriority={i === 0 ? "high" : undefined}
              className="w-full shrink-0 snap-start aspect-[4/3] sm:aspect-square object-cover"
            />
          ))}
        </div>
        {images.length > 1 && (
          <>
            <button type="button" aria-label="Previous photo" data-testid={`${idPrefix}gallery-prev`} onClick={() => go(active - 1)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white">
              <CaretLeft size={18} weight="bold" />
            </button>
            <button type="button" aria-label="Next photo" data-testid={`${idPrefix}gallery-next`} onClick={() => go(active + 1)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white">
              <CaretRight size={18} weight="bold" />
            </button>
            <span data-testid={`${idPrefix}gallery-counter`} className="absolute bottom-3 right-3 text-xs font-semibold bg-[#2C2B29]/80 text-white px-2.5 py-1 rounded-full">
              {active + 1} / {images.length}
            </span>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-3 mt-3">
          {images.map((src, i) => (
            <button
              key={src}
              type="button"
              data-testid={`${idPrefix}gallery-thumb-${i}`}
              aria-label={`Show photo ${i + 1}`}
              onClick={() => go(i)}
              className={`w-20 h-16 sm:w-24 sm:h-20 rounded-xl overflow-hidden border-2 transition-colors ${i === active ? "border-[#D17A58]" : "border-transparent opacity-70 hover:opacity-100"}`}
            >
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
